'use client'

import {
  IconButton,
  Portal,
  Slide,
  Snackbar as MuiSnackBar,
  SnackbarProps,
} from '@mui/material'
import { usePathname } from 'next/navigation'
import { reverse } from 'ramda'
import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { TransitionGroup } from 'react-transition-group'
import { create, StateCreator } from 'zustand'

import { Alert, AlertProps } from '../alert'
import { Icon } from '../icon'

export const Snackbar = (props: SnackbarProps) => <MuiSnackBar {...props} />

export type SnackbarState = {
  severity: AlertProps['severity']
  message: string | null
}

export const useSnackbar = () => {
  const [snackbarState, setSnackbarState] = useState<SnackbarState>({
    severity: 'success',
    message: null,
  })

  const closeSnackbar = useCallback(
    () => setSnackbarState({ severity: 'success', message: null }),
    [],
  )

  return {
    snackbarState,
    setSnackbarState,
    closeSnackbar,
  }
}

type Snack = PropsWithChildren<{
  open?: boolean
  alert?: {
    severity: AlertProps['severity']
    message: ReactNode
    action?: AlertProps['action']
  }
  snackbarProviderId: number
  dropOnRouteChange?: boolean
  routeChangeCount?: number
}>

const DEFAULT_SNACKBAR_OPTIONS = { limit: 3 }

const SnackbarStore =
  ({ limit = 3 } = DEFAULT_SNACKBAR_OPTIONS): StateCreator<{
    nextId: number
    snacks: Snack[]
    shift(snack: Omit<Snack, 'snackbarProviderId'>): number
    remove(id: number): void
    drop(): void
    dropOnRouteChange(): void
  }> =>
  (set, get) => ({
    nextId: 1,
    snacks: [],
    shift: snack => {
      const snackId = get().nextId

      set(state => ({
        ...state,
        nextId: state.nextId + 1,
        snacks: [
          {
            ...snack,
            snackbarProviderId: state.nextId,
            open: true,
            routeChangeCount: 0,
          },
          ...state.snacks,
        ].slice(0, limit),
      }))

      return snackId
    },
    remove: (id: number) => {
      set(state => ({
        ...state,
        snacks: state.snacks.filter(s => s.snackbarProviderId !== id),
      }))
    },
    drop: () => {
      set(state => ({
        ...state,
        snacks: [],
      }))
    },
    dropOnRouteChange: () => {
      set(state => ({
        ...state,
        snacks: state.snacks
          .map(s => ({
            ...s,
            routeChangeCount: (s.routeChangeCount || 0) + 1,
            dropOnRouteChange: true,
          }))
          .filter(s => (s.routeChangeCount || 0) < 2),
      }))
    },
  })

const globalStore = create(SnackbarStore({ limit: 3 }))

const SnackbarContext = createContext(globalStore)

export function SnackbarProvider({
  store = globalStore,
  children,
  ...props
}: Omit<SnackbarProps, 'children'> &
  PropsWithChildren<{ store?: typeof globalStore }>) {
  const snacks = store(state => state.snacks)
  const remove = store(state => state.remove)
  const dropOnRouteChange = store(state => state.dropOnRouteChange)

  const pathname = usePathname()

  useEffect(() => {
    dropOnRouteChange()
    // oxlint-disable-next-line react/exhaustive-deps -- only update on route change
  }, [pathname])

  // auto-focus the latest dismiss button
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const dismissButtons = document.querySelectorAll<HTMLButtonElement>(
        '.snackbar-dismiss-btn',
      )
      const lastButton = dismissButtons[dismissButtons.length - 1]
      if (lastButton) lastButton?.focus()
    }, 200)

    return () => clearTimeout(timeoutId)
  }, [snacks])

  return (
    <SnackbarContext.Provider value={store}>
      {children}
      {/*
        Portal to document.body, where MUI dialogs render, so the snackbar's
        z-index can stack above an open dialog. Rendered in place, it would be
        trapped in any ancestor stacking context, such as mobile's <ion-app>.
      */}
      <Portal>
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          {...props}
          open={!!snacks.length}
        >
          <div>
            <TransitionGroup>
              {reverse(snacks).map(
                ({ snackbarProviderId, alert, children }) => (
                  <Slide direction="up" key={snackbarProviderId}>
                    <div style={{ marginTop: 16 }}>
                      {alert ? (
                        <Alert
                          severity={alert.severity}
                          action={
                            alert.action ?? (
                              <IconButton
                                className="snackbar-dismiss-btn"
                                onClick={() => remove(snackbarProviderId)}
                                aria-label="dismiss"
                                sx={theme => ({
                                  marginLeft: theme.spacing(1),
                                  '&:focus': {
                                    backgroundColor:
                                      theme.palette.brand.grey.main,
                                  },
                                })}
                              >
                                <Icon icon="Cross" size="md" />
                              </IconButton>
                            )
                          }
                        >
                          {alert.message}
                        </Alert>
                      ) : (
                        children
                      )}
                    </div>
                  </Slide>
                ),
              )}
            </TransitionGroup>
          </div>
        </Snackbar>
      </Portal>
    </SnackbarContext.Provider>
  )
}

export function useGlobalSnackbarStore() {
  return useContext(SnackbarContext)
}

export function useClearSnacks() {
  return useContext(SnackbarContext)(store => store.drop)
}

export function usePushSnack() {
  return useContext(SnackbarContext)(store => store.shift)
}
